import React, { useState, useEffect } from "react";
import {
  FileText,
  Download,
  Calendar,
  CheckSquare,
  AlertTriangle,
  Users,
  Shield,
  Loader2,
  FileDown,
  RefreshCw,
  Database,
  FileSpreadsheet,
  File,
  CheckCircle,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { logExport, AUDIT_TYPES } from "../lib/auditLogger";

// Import dynamique pour gérer les erreurs
let jsPDF = null;
let autoTable = null;
let XLSX = null;

try {
  jsPDF = require("jspdf").default || require("jspdf");
  autoTable = require("jspdf-autotable").default || require("jspdf-autotable");
  XLSX = require("xlsx");
} catch (e) {
  console.warn("Librairies PDF/Excel non disponibles:", e);
}

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadReportData = async (entrepriseId) => {
  const data = {
    inventaire: [],
    documents: [],
    incidents: [],
    efvp: [],
    consentements: [],
    utilisateurs: [],
  };

  try {
    const { data: inventaire } = await supabase
      .from("inventaire_donnees")
      .select("*")
      .eq("entreprise_id", entrepriseId)
      .order("created_at", { ascending: false });
    data.inventaire = inventaire || [];

    const { data: documents } = await supabase
      .from("documents")
      .select("*")
      .eq("entreprise_id", entrepriseId);
    data.documents = documents || [];

    const { data: incidents } = await supabase
      .from("incidents")
      .select("*")
      .eq("entreprise_id", entrepriseId);
    data.incidents = incidents || [];

    const { data: efvp } = await supabase
      .from("efvp")
      .select("*")
      .eq("entreprise_id", entrepriseId);
    data.efvp = efvp || [];

    const { data: consentements } = await supabase
      .from("consentements")
      .select("*")
      .eq("entreprise_id", entrepriseId);
    data.consentements = consentements || [];

    const { data: utilisateurs } = await supabase
      .from("profils")
      .select("*")
      .eq("organisation_id", entrepriseId);
    data.utilisateurs = utilisateurs || [];
  } catch (error) {
    console.error("Erreur chargement:", error);
  }

  return data;
};

// ============================================
// GÉNÉRATION PDF
// ============================================

const generatePDF = (reportType, data, companyName, dateRange) => {
  if (!jsPDF) {
    throw new Error("jsPDF non disponible");
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPos = margin;

  // Couleurs
  const navy = [30, 58, 95];
  const skyBlue = [14, 165, 233];
  const gray = [100, 116, 139];

  // Header
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageWidth, 35, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(getReportTitle(reportType), margin, 20);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`${companyName} - Conformité Loi 25`, margin, 28);

  // Logo
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(pageWidth - 45, 10, 30, 15, 2, 2, "F");
  doc.setTextColor(...navy);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.text("CoryntiQ", pageWidth - 40, 19);

  yPos = 45;

  // Info box
  doc.setFillColor(240, 249, 255);
  doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 2, 2, "F");
  doc.setDrawColor(...skyBlue);
  doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 2, 2, "S");
  doc.setTextColor(...navy);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("Informations", margin + 5, yPos + 8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...gray);
  const dateStr = new Date().toLocaleDateString("fr-CA");
  doc.text(
    `Date: ${dateStr}  |  Entreprise: ${companyName}`,
    margin + 5,
    yPos + 15
  );
  yPos += 30;

  // Section title helper
  const addSection = (title) => {
    doc.setFillColor(...skyBlue);
    doc.rect(margin, yPos, 3, 7, "F");
    doc.setTextColor(...navy);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(title, margin + 6, yPos + 5);
    yPos += 12;
  };

  // Stats helper
  const addStats = (stats) => {
    const cardW = (pageWidth - 2 * margin - 9) / 4;
    let x = margin;
    stats.forEach((s, i) => {
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, yPos, cardW, 20, 2, 2, "F");
      doc.setTextColor(...navy);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(String(s.value), x + 5, yPos + 10);
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.text(s.label, x + 5, yPos + 16);
      x += cardW + 3;
    });
    yPos += 28;
  };

  // Table helper avec autoTable
  const addTable = (headers, rows) => {
    if (rows.length === 0) {
      doc.setTextColor(...gray);
      doc.setFontSize(9);
      doc.text("Aucune donnée", margin, yPos);
      yPos += 10;
      return;
    }

    if (autoTable) {
      autoTable(doc, {
        startY: yPos,
        head: [headers],
        body: rows,
        margin: { left: margin, right: margin },
        headStyles: {
          fillColor: navy,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        bodyStyles: { fontSize: 7, textColor: [51, 65, 85] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: { cellPadding: 3 },
      });
      yPos = doc.lastAutoTable.finalY + 10;
    } else {
      // Fallback sans autoTable
      doc.setFontSize(8);
      doc.setTextColor(...navy);
      headers.forEach((h, i) => {
        doc.text(h, margin + i * 35, yPos);
      });
      yPos += 5;
      doc.setTextColor(...gray);
      rows.slice(0, 10).forEach((row) => {
        row.forEach((cell, i) => {
          doc.text(String(cell || "-").substring(0, 15), margin + i * 35, yPos);
        });
        yPos += 5;
      });
      yPos += 5;
    }
  };

  // Build content based on report type
  switch (reportType) {
    case "conformite": {
      let score = 0;
      if (data.inventaire.length > 0) score += 20;
      if (data.documents.length > 0) score += 20;
      if (data.efvp.length > 0) score += 20;
      if (data.consentements.length > 0) score += 20;
      if (
        data.incidents.length === 0 ||
        data.incidents.some((i) => i.statut === "Résolu")
      )
        score += 20;

      addSection("Résumé de conformité");
      addStats([
        { label: "Score", value: `${score}%` },
        { label: "Inventaire", value: data.inventaire.length },
        { label: "Documents", value: data.documents.length },
        { label: "Utilisateurs", value: data.utilisateurs.length },
      ]);

      addSection("Par module");
      addStats([
        { label: "EFVP", value: data.efvp.length },
        { label: "Incidents", value: data.incidents.length },
        { label: "Consentements", value: data.consentements.length },
        {
          label: "Résolus",
          value: data.incidents.filter((i) => i.statut === "Résolu").length,
        },
      ]);

      addSection("Recommandations");
      const recs = [];
      if (data.inventaire.length === 0)
        recs.push(["✗ Créer un inventaire des données"]);
      if (data.documents.length === 0)
        recs.push(["✗ Ajouter une politique de confidentialité"]);
      if (data.efvp.length === 0) recs.push(["✗ Réaliser une EFVP"]);
      if (recs.length === 0) recs.push(["✓ Bon travail! Continuez ainsi."]);
      addTable(["Action"], recs);
      break;
    }

    case "inventaire": {
      addSection("Inventaire des données");
      addStats([
        { label: "Total", value: data.inventaire.length },
        {
          label: "Sensibles",
          value: data.inventaire.filter((i) =>
            (i.sensibilite || "").toLowerCase().includes("confidentiel")
          ).length,
        },
      ]);
      const rows = data.inventaire.map((i) => [
        i.nom || "-",
        i.type || "-",
        i.sensibilite || "-",
        i.responsable || "-",
      ]);
      addTable(["Nom", "Type", "Sensibilité", "Responsable"], rows);
      break;
    }

    case "incidents": {
      addSection("Incidents");
      addStats([
        { label: "Total", value: data.incidents.length },
        {
          label: "Résolus",
          value: data.incidents.filter((i) => i.statut === "Résolu").length,
        },
        {
          label: "En cours",
          value: data.incidents.filter((i) => i.statut === "En cours").length,
        },
        {
          label: "CAI",
          value: data.incidents.filter((i) => i.cai_notifiee).length,
        },
      ]);
      const rows = data.incidents.map((i) => [
        i.type || "-",
        i.severite || "-",
        i.statut || "-",
        i.cai_notifiee ? "Oui" : "Non",
      ]);
      addTable(["Type", "Sévérité", "Statut", "CAI"], rows);
      break;
    }

    case "efvp": {
      addSection("EFVP");
      addStats([
        { label: "Total", value: data.efvp.length },
        {
          label: "Terminées",
          value: data.efvp.filter((e) => e.statut === "Terminé").length,
        },
        {
          label: "En cours",
          value: data.efvp.filter((e) => e.statut === "En cours").length,
        },
      ]);
      const rows = data.efvp.map((e) => [
        e.nom_projet || "-",
        e.niveau_risque || "-",
        e.statut || "-",
        `${e.progression || 0}%`,
      ]);
      addTable(["Projet", "Risque", "Statut", "Progression"], rows);
      break;
    }

    case "consentements": {
      addSection("Consentements");
      addStats([
        { label: "Total", value: data.consentements.length },
        {
          label: "Actifs",
          value: data.consentements.filter((c) => c.actif).length,
        },
      ]);
      const rows = data.consentements.map((c) => [
        c.type_consentement || "-",
        c.methode_obtention || "-",
        c.actif ? "Actif" : "Inactif",
      ]);
      addTable(["Type", "Méthode", "Statut"], rows);
      break;
    }

    case "complet": {
      // Page 1: Conformité
      let score = 0;
      if (data.inventaire.length > 0) score += 20;
      if (data.documents.length > 0) score += 20;
      if (data.efvp.length > 0) score += 20;
      if (data.consentements.length > 0) score += 20;
      if (data.incidents.length === 0) score += 20;

      addSection("Vue d'ensemble");
      addStats([
        { label: "Score", value: `${score}%` },
        { label: "Inventaire", value: data.inventaire.length },
        { label: "EFVP", value: data.efvp.length },
        { label: "Incidents", value: data.incidents.length },
      ]);

      addSection("Inventaire (extrait)");
      const invRows = data.inventaire
        .slice(0, 5)
        .map((i) => [i.nom || "-", i.sensibilite || "-"]);
      addTable(["Nom", "Sensibilité"], invRows);

      if (data.incidents.length > 0) {
        addSection("Incidents");
        const incRows = data.incidents
          .slice(0, 5)
          .map((i) => [i.type || "-", i.statut || "-"]);
        addTable(["Type", "Statut"], incRows);
      }
      break;
    }
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footerY = doc.internal.pageSize.getHeight() - 10;
    doc.setDrawColor(...gray);
    doc.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.text(`Confidentiel - CoryntiQ - ${dateStr}`, margin, footerY);
    doc.text(`Page ${i}/${pageCount}`, pageWidth - margin - 15, footerY);
  }

  return doc;
};

const getReportTitle = (type) => {
  const titles = {
    conformite: "Rapport de Conformité",
    inventaire: "Inventaire des Données",
    incidents: "Rapport des Incidents",
    efvp: "Rapport EFVP",
    consentements: "Rapport Consentements",
    complet: "Rapport Complet",
  };
  return titles[type] || "Rapport";
};

// ============================================
// GÉNÉRATION EXCEL
// ============================================

const generateExcel = (reportType, data, companyName) => {
  if (!XLSX) throw new Error("XLSX non disponible");

  const wb = XLSX.utils.book_new();

  // Résumé
  const summary = [
    ["Rapport CoryntiQ - Conformité Loi 25"],
    ["Entreprise:", companyName],
    ["Date:", new Date().toLocaleDateString("fr-CA")],
    [""],
    ["Module", "Nombre"],
    ["Inventaire", data.inventaire.length],
    ["Documents", data.documents.length],
    ["EFVP", data.efvp.length],
    ["Incidents", data.incidents.length],
    ["Consentements", data.consentements.length],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Résumé");

  // Ajouter les feuilles selon le type
  if (
    data.inventaire.length > 0 &&
    ["inventaire", "complet", "conformite"].includes(reportType)
  ) {
    const inv = [
      [
        "Nom",
        "Type",
        "Sensibilité",
        "Localisation",
        "Responsable",
        "Rétention",
      ],
    ];
    data.inventaire.forEach((i) =>
      inv.push([
        i.nom,
        i.type,
        i.sensibilite,
        i.localisation,
        i.responsable,
        i.retention,
      ])
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(inv),
      "Inventaire"
    );
  }

  if (
    data.incidents.length > 0 &&
    ["incidents", "complet"].includes(reportType)
  ) {
    const inc = [["Type", "Date", "Sévérité", "Statut", "CAI"]];
    data.incidents.forEach((i) =>
      inc.push([
        i.type,
        i.date_incident,
        i.severite,
        i.statut,
        i.cai_notifiee ? "Oui" : "Non",
      ])
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(inc), "Incidents");
  }

  if (data.efvp.length > 0 && ["efvp", "complet"].includes(reportType)) {
    const efvp = [["Projet", "Risque", "Statut", "Progression", "Responsable"]];
    data.efvp.forEach((e) =>
      efvp.push([
        e.nom_projet,
        e.niveau_risque,
        e.statut,
        `${e.progression || 0}%`,
        e.responsable,
      ])
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(efvp), "EFVP");
  }

  if (
    data.consentements.length > 0 &&
    ["consentements", "complet"].includes(reportType)
  ) {
    const cons = [["Type", "Date", "Méthode", "Actif"]];
    data.consentements.forEach((c) =>
      cons.push([
        c.type_consentement,
        c.date_obtention,
        c.methode_obtention,
        c.actif ? "Oui" : "Non",
      ])
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(cons),
      "Consentements"
    );
  }

  return wb;
};

// ============================================
// GÉNÉRATION CSV
// ============================================

const generateCSV = (reportType, data) => {
  let rows = [];

  switch (reportType) {
    case "inventaire":
      rows = [
        ["Nom", "Type", "Sensibilité", "Responsable"],
        ...data.inventaire.map((i) => [
          i.nom,
          i.type,
          i.sensibilite,
          i.responsable,
        ]),
      ];
      break;
    case "incidents":
      rows = [
        ["Type", "Sévérité", "Statut", "CAI"],
        ...data.incidents.map((i) => [
          i.type,
          i.severite,
          i.statut,
          i.cai_notifiee ? "Oui" : "Non",
        ]),
      ];
      break;
    case "efvp":
      rows = [
        ["Projet", "Risque", "Statut", "Progression"],
        ...data.efvp.map((e) => [
          e.nom_projet,
          e.niveau_risque,
          e.statut,
          `${e.progression || 0}%`,
        ]),
      ];
      break;
    case "consentements":
      rows = [
        ["Type", "Méthode", "Actif"],
        ...data.consentements.map((c) => [
          c.type_consentement,
          c.methode_obtention,
          c.actif ? "Oui" : "Non",
        ]),
      ];
      break;
    default:
      rows = [
        ["Module", "Nombre"],
        ["Inventaire", data.inventaire.length],
        ["Incidents", data.incidents.length],
        ["EFVP", data.efvp.length],
        ["Consentements", data.consentements.length],
      ];
  }

  return rows
    .map((row) =>
      row
        .map((cell) => `"${(cell || "").toString().replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");
};

// ============================================
// COMPOSANT
// ============================================

const ReportsGenerator = ({ showToast, entrepriseId, entrepriseNom }) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState("pdf");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [libsAvailable, setLibsAvailable] = useState({
    pdf: !!jsPDF,
    excel: !!XLSX,
  });

  const reportTypes = [
    {
      id: "conformite",
      name: "Rapport de conformité",
      desc: "Vue d'ensemble Loi 25",
      icon: Shield,
      color: "blue",
    },
    {
      id: "inventaire",
      name: "Inventaire des données",
      desc: "Données personnelles",
      icon: Database,
      color: "cyan",
    },
    {
      id: "incidents",
      name: "Rapport d'incidents",
      desc: "Incidents de sécurité",
      icon: AlertTriangle,
      color: "red",
    },
    {
      id: "efvp",
      name: "Rapport EFVP",
      desc: "Évaluations vie privée",
      icon: FileText,
      color: "purple",
    },
    {
      id: "consentements",
      name: "Audit consentements",
      desc: "Historique",
      icon: Users,
      color: "green",
    },
    {
      id: "complet",
      name: "Rapport complet",
      desc: "Tous les modules",
      icon: FileDown,
      color: "indigo",
    },
  ];

  const formatOptions = [
    { id: "pdf", name: "PDF", icon: File, available: libsAvailable.pdf },
    {
      id: "excel",
      name: "Excel",
      icon: FileSpreadsheet,
      available: libsAvailable.excel,
    },
    { id: "csv", name: "CSV", icon: FileText, available: true },
  ];

  useEffect(() => {
    if (entrepriseId) loadData();
    else setIsLoading(false);
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const data = await loadReportData(entrepriseId);
      setReportData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedReport || !reportData) {
      showToast?.("Sélectionnez un rapport.", "warning");
      return;
    }

    setIsGenerating(true);
    try {
      const companyName = entrepriseNom || "Mon entreprise";
      const name =
        reportTypes.find((r) => r.id === selectedReport)?.name || "Rapport";
      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `${name.replace(/\s+/g, "_")}_${dateStr}`;

      if (selectedFormat === "pdf") {
        const doc = generatePDF(
          selectedReport,
          reportData,
          companyName,
          dateRange
        );
        doc.save(`${filename}.pdf`);
      } else if (selectedFormat === "excel") {
        const wb = generateExcel(selectedReport, reportData, companyName);
        XLSX.writeFile(wb, `${filename}.xlsx`);
      } else {
        const csv = generateCSV(selectedReport, reportData);
        const blob = new Blob(["\ufeff" + csv], {
          type: "text/csv;charset=utf-8",
        });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${filename}.csv`;
        a.click();
      }

      // Logger l'export
      await logExport(entrepriseId, AUDIT_TYPES.RAPPORT, selectedFormat, {
        reportType: selectedReport,
        reportName: name,
        inventaireCount: reportData.inventaire?.length || 0,
        incidentsCount: reportData.incidents?.length || 0,
      });

      showToast?.(
        `${name} généré en ${selectedFormat.toUpperCase()}!`,
        "success"
      );
    } catch (err) {
      console.error(err);
      showToast?.(`Erreur: ${err.message}`, "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const colors = {
    blue: "from-blue-500 to-blue-600",
    red: "from-red-500 to-red-600",
    purple: "from-purple-500 to-purple-600",
    green: "from-green-500 to-green-600",
    indigo: "from-indigo-500 to-indigo-600",
    cyan: "from-cyan-500 to-cyan-600",
  };

  if (!entrepriseId) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-amber-500 mx-auto mb-4" />
        <p className="text-amber-700 dark:text-amber-300">
          Sélectionnez une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center py-12">
        <Loader2 size={40} className="text-sky-600 animate-spin mb-4" />
        <p className="text-gray-500">Chargement...</p>
      </div>
    );
  }

  const stats = reportData || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            Générateur de rapports
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Exportez en PDF, Excel ou CSV
          </p>
        </div>
        <button
          onClick={loadData}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Librairies status */}
      {(!libsAvailable.pdf || !libsAvailable.excel) && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-2">
            ⚠️ Librairies manquantes
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {!libsAvailable.pdf && "PDF non disponible. "}
            {!libsAvailable.excel && "Excel non disponible. "}
            Installez:{" "}
            <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">
              npm install jspdf jspdf-autotable xlsx
            </code>
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-500 mt-2">
            Le format CSV fonctionne toujours.
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-4">
        <p className="text-sm font-medium text-sky-800 dark:text-sky-300 mb-2">
          Données disponibles :
        </p>
        <div className="flex flex-wrap gap-4 text-sm text-sky-700 dark:text-sky-400">
          <span>📊 {stats.inventaire?.length || 0} inventaire</span>
          <span>📄 {stats.documents?.length || 0} documents</span>
          <span>🛡️ {stats.efvp?.length || 0} EFVP</span>
          <span>⚠️ {stats.incidents?.length || 0} incidents</span>
          <span>✅ {stats.consentements?.length || 0} consentements</span>
        </div>
      </div>

      {/* Report types */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
          1. Type de rapport
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportTypes.map((r) => {
            const Icon = r.icon;
            const sel = selectedReport === r.id;
            return (
              <div
                key={r.id}
                onClick={() => setSelectedReport(r.id)}
                className={`cursor-pointer rounded-xl p-5 transition-all ${
                  sel
                    ? `bg-gradient-to-br ${
                        colors[r.color]
                      } text-white shadow-lg scale-[1.02]`
                    : "bg-white dark:bg-gray-800 hover:shadow-md border border-gray-200 dark:border-gray-700"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <Icon
                    size={28}
                    className={sel ? "text-white" : "text-gray-400"}
                  />
                  {sel && <CheckSquare size={22} className="text-white" />}
                </div>
                <h4
                  className={`font-bold mb-1 ${
                    sel ? "text-white" : "text-gray-900 dark:text-white"
                  }`}
                >
                  {r.name}
                </h4>
                <p
                  className={`text-sm ${
                    sel ? "text-white/80" : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {r.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Format selection */}
      {selectedReport && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            2. Format
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {formatOptions.map((f) => {
              const Icon = f.icon;
              const sel = selectedFormat === f.id;
              const disabled = !f.available;
              return (
                <button
                  key={f.id}
                  onClick={() => !disabled && setSelectedFormat(f.id)}
                  disabled={disabled}
                  className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                    disabled
                      ? "border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed"
                      : sel
                      ? "border-sky-500 bg-sky-50 dark:bg-sky-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon
                    size={28}
                    className={sel ? "text-sky-600" : "text-gray-400"}
                  />
                  <span
                    className={`font-medium mt-2 ${
                      sel
                        ? "text-sky-700 dark:text-sky-400"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {f.name}
                  </span>
                  {disabled && (
                    <span className="text-xs text-red-500 mt-1">
                      Non disponible
                    </span>
                  )}
                  {f.id === "csv" && (
                    <span className="text-xs text-green-600 mt-1">
                      ✓ Toujours dispo
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Period */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Période (optionnel)
            </h4>
            <div className="flex gap-4 items-center">
              <Calendar size={16} className="text-gray-400" />
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) =>
                  setDateRange({ ...dateRange, start: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange({ ...dateRange, end: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Generate button */}
      {selectedReport && (
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setSelectedReport(null)}
            className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Annuler
          </button>
          <button
            onClick={handleGenerate}
            disabled={
              isGenerating ||
              (selectedFormat === "pdf" && !libsAvailable.pdf) ||
              (selectedFormat === "excel" && !libsAvailable.excel)
            }
            className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2 disabled:opacity-50 shadow-lg"
          >
            {isGenerating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span>Génération...</span>
              </>
            ) : (
              <>
                <Download size={20} />
                <span>Générer {selectedFormat.toUpperCase()}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportsGenerator;
